import { useState } from "react";
import { Pen, Trash2, Paperclip } from "lucide-react";
import { FaUserCircle } from "react-icons/fa";
import { useStateValue } from "../../context/StateProvider";
import AgentDropdown from "../deals-board/agentDropdown";
import { saveTaskTemplate } from "../../utils/firebasefunctions";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/firebaseConfig";

const TaskCard = ({
  task,
  tasks,
  setTasks,
  status, // ✅ IMPORTANT
  editingTaskId,
  setEditingTaskId,
}) => {
  const [{ users }] = useStateValue();

  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);

  const allAgent = users.filter(
    (u) =>
      (u.role === "Employee" ||
        u.role === "Admin" ||
        u.role === "Supervisor") &&
      u.type !== 1 &&
      u.type !== 2,
  );

  const safeISOString = (date) => {
    const d = new Date(date);
    return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  };

  const getFileType = (fileName) => {
    if (!fileName) return "File";
    return fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? "Image" : "File";
  };

  const getCleanTasks = (updatedTasks) => {
    return Object.keys(updatedTasks).reduce((acc, key) => {
      acc[key] = (updatedTasks[key] || []).map((t) => ({
        id: t.id,
        title: t.title || "",

        // ✅ fixed status (always Open)
        status: "Open",

        deadline: t.deadline ? String(t.deadline) : "",

        // ✅ attachments safe
        attachments: Array.isArray(t.attachments)
          ? t.attachments.map((a) => ({
              name: a.name || "",
              type: a.type || "",
              size: a.size || 0,
              url: a.url || "",
            }))
          : [],

        assignee: t.assignee?.id
          ? {
              id: t.assignee.id,
              firstname: t.assignee.firstname || "",
              lastname: t.assignee.lastname || "",
              profilepicture: t.assignee.profilepicture || "",
              role: t.assignee.role || "",
            }
          : null,

        // ✅ FIXED CRASH HERE
        createdAt: t.createdAt
          ? safeISOString(t.createdAt)
          : safeISOString(Date.now()),
      }));

      return acc;
    }, {});
  };

  const handleAttachment = async (e, task) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const fileId = Date.now() + Math.random();

      const storageRef = ref(
        storage,
        `tasks/${task.id}/${fileId}-${file.name}`,
      );

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const newAttachment = {
        id: fileId,
        name: file.name,
        type: file.type.startsWith("image/") ? "image" : "file",
        url,
        path: storageRef.fullPath,
      };

      // ✅ IMPORTANT FIX (functional update)
      setTasks((prev) => {
        const updated = {
          ...prev,
          [status]: (prev[status] || []).map((t) =>
            t.id === task.id
              ? {
                  ...t,
                  attachments: [...(t.attachments || []), newAttachment],
                }
              : t,
          ),
        };

        // ✅ SAVE AFTER STATE IS CORRECT
        saveTaskTemplate(getCleanTasks(updated));

        return updated;
      });
    } catch (error) {
      console.log("Attachment Upload Error:", error);
    }
  };

  // ✅ DELETE
  const handleDeleteTask = async () => {
    const updatedStatusTasks = (tasks[status] || []).filter(
      (t) => t.id !== task.id,
    );

    const updatedTasks = {
      ...tasks,
      [status]: updatedStatusTasks,
    };

    setTasks(updatedTasks);
    await saveTaskTemplate(updatedTasks);
  };

  // ✅ SAVE TITLE
  const handleSaveTask = async (task) => {
    const trimmed = task.title.trim();

    let updatedStatusTasks;

    if (!trimmed) {
      updatedStatusTasks = (tasks[status] || []).filter(
        (t) => t.id !== task.id,
      );
    } else {
      updatedStatusTasks = (tasks[status] || []).map((t) =>
        t.id === task.id ? { ...t, title: trimmed } : t,
      );
    }

    const updatedTasks = {
      ...tasks,
      [status]: updatedStatusTasks,
    };

    // ✅ UI update
    setTasks(updatedTasks);
    setEditingTaskId(null);

    try {
      const cleanTasks = Object.keys(updatedTasks).reduce((acc, key) => {
        acc[key] = (updatedTasks[key] || []).map((t) => ({
          id: t.id,
          title: t.title || "",

          // ✅ ALWAYS OPEN (as per your requirement)
          status: "Open",

          // ✅ safe values
          deadline: t.deadline ? String(t.deadline) : "",
          attachments: Array.isArray(t.attachments) ? t.attachments : [],

          // ✅ minimal assignee only
          assignee:
            t.assignee && t.assignee.id
              ? {
                  id: t.assignee.id,
                  firstname: t.assignee.firstname,
                  lastname: t.assignee.lastname,
                  profilepicture: t.assignee.profilepicture || "",
                  role: t.assignee.role,
                }
              : null,

          // ✅ preserve old createdAt (important fix)
          createdAt: t.createdAt ? new Date(t.createdAt).toISOString() : null,
        }));
        return acc;
      }, {});

      await saveTaskTemplate(cleanTasks);
    } catch (err) {
      console.log("Template Save Error", err);
    }
  };

  return (
    <div className="border-t border-b border-gray-200 p-2 group">
      <div className="flex justify-between items-center gap-2 lg:gap-3">
        {/* TITLE */}
        <div className="w-[45%] lg:w-[45%]">
          {editingTaskId === task.id ? (
            <input
              className="border px-2 py-1 rounded w-full text-sm"
              value={task.title}
              onChange={(e) => {
                const updatedStatusTasks = (tasks[status] || []).map((t) =>
                  t.id === task.id ? { ...t, title: e.target.value } : t,
                );

                setTasks((prev) => ({
                  ...prev,
                  [status]: updatedStatusTasks,
                }));
              }}
              onBlur={() => handleSaveTask(task)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSaveTask(task);
                }
              }}
              autoFocus
            />
          ) : (
            <p
              className="cursor-pointer font-semibold text-sm text-gray-800 hover:text-blue-600"
              onClick={() => setEditingTaskId(task.id)}
            >
              {task.title || "Untitled Task"}
            </p>
          )}
        </div>

        {/* ASSIGNEE */}
        <div className="w-[20%] lg:w-[20%] relative">
          <div
            className="flex items-center justify-center gap-2 cursor-pointer relative group"
            onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
          >
            {/* AVATAR */}
            {task.assignee?.profilepicture ? (
              <img
                src={task.assignee.profilepicture}
                className="w-6 h-6 rounded-full ring-2 ring-white shadow"
                alt="assignee"
              />
            ) : (
              <FaUserCircle className="w-6 h-6 text-gray-400" />
            )}

            {/* HOVER TOOLTIP */}
            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
              {task.assignee
                ? `${task.assignee.firstname || ""} ${task.assignee.lastname || ""}`
                : "Unassigned"}
            </div>
          </div>

          {/* DROPDOWN */}
          {showAssigneeDropdown && (
            <AgentDropdown
              visible
              data={allAgent}
              onSelect={async (agent) => {
                const minimalAgent = agent
                  ? {
                      id: agent.id,
                      firstname: agent.firstname,
                      lastname: agent.lastname,
                      profilepicture: agent.profilepicture || "",
                      role: agent.role,
                    }
                  : null;

                setTasks((prev) => {
                  const updatedStatusTasks = (prev[status] || []).map((t) => {
                    if (t.id !== task.id) return t;

                    return {
                      ...t,
                      assignee: minimalAgent,
                    };
                  });

                  const updated = {
                    ...prev,
                    [status]: updatedStatusTasks,
                  };

                  // save AFTER correct state
                  saveTaskTemplate(getCleanTasks(updated));

                  return updated;
                });

                setShowAssigneeDropdown(false);
              }}
              onClose={() => setShowAssigneeDropdown(false)}
            />
          )}
        </div>
        {/* ATTACHMENTS */}
        <div className="w-[20%] flex gap-2 justify-center items-center">
          <label className="cursor-pointer">
            <Paperclip className="w-4 h-4 text-gray-500 hover:text-black" />
            <input
              type="file"
              className="hidden"
              onChange={(e) => handleAttachment(e, task)}
            />
          </label>

          {task.attachments?.length > 0 && (
            <div className="flex gap-1">
              {task.attachments.slice(0, 2).map((att, i) => (
                <div key={i} className="relative group">
                  <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded">
                    {att.type === "image" ? "IMG" : "FILE"}
                  </span>

                  {/* hover name */}
                  <div className="absolute bottom-full mb-1 hidden group-hover:block bg-black text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                    {att.name}
                  </div>
                </div>
              ))}

              {task.attachments.length > 2 && (
                <span className="text-[10px] text-gray-500">
                  +{task.attachments.length - 2}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ACTIONS */}
        <div className="w-[15%] lg:w-[15%] flex justify-center gap-2">
          {/* <Pen
            size={18}
            className="cursor-pointer text-gray-400 hover:text-black"
            onClick={() => setEditingTaskId(task.id)}
          /> */}
          <Trash2
            size={18}
            className="cursor-pointer text-gray-400 hover:text-red-500"
            onClick={handleDeleteTask}
          />
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
